import React, { useState, useEffect } from "react";
import { ReactSVG } from "react-svg";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";

import { redFlagSymptomsEnum } from "./data";
import {
  setClinicalServiceSteps,
  updateClinicalService,
} from "../../../../../redux/modules/clinical-services/actions";
import Button from "../../../../common/Button";
import EndServiceModel from "../../../../common/EndServiceModel";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import Steps from "../../Steps";
import {
  AlertConfirmationTypes,
  MinorAilmentStep,
} from "../../../../../helpers/constant";
import SubNavigation from "../../common/sub-navigation";
import { getValidStep, updateStepsValidity } from "../../../text/data";

const NonTypical = () => {
  const { clinicalServiceObj, clinicalServiceSteps } = useSelector(
    (state) => state.clinicalService
  );
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const { id: patientId, serviceId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [clinicalAnalysis, setClinicalAnalysis] = useState(false);
  const [acuteMCVals, setAcuteMCVals] = useState([]);
  const [showAnalysisCard, setShowAnalysisCard] = useState(false);
  const [completeSubStep, setCompleteSubStep] = useState(false);
  const [nonTypicalObj, setNonTypicalObj] = useState(null);
  const [showOTC, setShowOTC] = useState(true);
  const [showEndService, setShowEndService] = useState(true);
  const { showReferral } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [showRef, setShowRef] = useState(showReferral ?? false);

  const [openMultiConditionDialog, setOpenMultiConditionDialog] =
    useState(false);
  const [alterContent, setAlterContent] = useState("");
  const [clickedEndService, setClickedEndService] = useState(false);
  const [alertConfirmation, setAlertConfirmation] = useState(0);
  const [alertExplaination, setAlertExplaination] = useState("");

  //state management
  useEffect(() => {
    if (clickedEndService) {
      onNext();
      setClickedEndService(false);
    }
  }, [clinicalServiceSteps, clickedEndService]);

  useEffect(() => {
    setNonTypicalObj({
      ...nonTypicalObj,
      acuteMCVals: acuteMCVals,
    });

    if (acuteMCVals.length > 0) {
      setShowAnalysisCard(true);
    } else setShowAnalysisCard(false);
  }, [acuteMCVals]);

  const resetAnalysis = () => {
    setClinicalAnalysis(false);
    setCompleteSubStep(false);
  };

  useEffect(() => {
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !==
        undefined &&
      clinicalServiceObj.initialAssessment.initialAssessmentForm
        .signsAndSymptoms !== null &&
      clinicalServiceObj.initialAssessment.initialAssessmentForm
        ?.signsAndSymptoms?.redFlagSymptoms !== null
    ) {
      setAcuteMCVals(
        clinicalServiceObj.initialAssessment.initialAssessmentForm
          ?.signsAndSymptoms?.redFlagSymptoms?.length > 0
          ? clinicalServiceObj.initialAssessment.initialAssessmentForm
              ?.signsAndSymptoms.redFlagSymptoms
          : ["none"]
      );
      setCompleteSubStep(true);
      setClinicalAnalysis(true);
    }
  }, [clinicalServiceObj]);

  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );
      const updatedSteps = {
        signsAndSymptoms: {
          redFlagSymptoms: acuteMCVals.includes("none") ? [] : acuteMCVals,
          nonTypicalConfirmationType: alertConfirmation,
          nonTypicalExplaination: alertExplaination,
        },
        currentStep: nextStep,
        currentSubStep: nextSubStep,
        showReferral: showRef,
        serviceSteps: clinicalServiceSteps,
      };
      dispatch(updateClinicalService(serviceId, updatedSteps));
    }
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  const updateStepsVisibility = async (disable = false) => {
    const updatedServiceSteps = updateStepsValidity(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      disable
    );
    await dispatch(setClinicalServiceSteps(updatedServiceSteps));
  };

  const acuteMultiSelection = (val) => {
    resetAnalysis();
    if (val === "none") {
      !acuteMCVals.includes(val)
        ? setAcuteMCVals(["none"])
        : setAcuteMCVals([]);
    } else {
      if (acuteMCVals.includes("none")) {
        setAcuteMCVals([val]);
      } else {
        acuteMCVals.includes(val)
          ? setAcuteMCVals(acuteMCVals.filter((item) => item !== val))
          : setAcuteMCVals([...acuteMCVals, val]);
      }
    }
  };
  const handleAlerts = () => {
    if (nonTypicalObj.acuteMCVals.includes("none")) {
      setShowOTC(false);
      setShowEndService(false);
      setAlterContent(<p>No Red Flag Signs & Symptoms Identified</p>);
    } else {
      setShowOTC(true);
      setShowEndService(true);
      setAlterContent(
        <>
          <p className="text-base font-semibold text-gray-600">
            Red Flag Signs & Symptoms Identified
            {acuteMCVals?.length ? (
              <>
                <br />[
                {acuteMCVals
                  .map?.(
                    (v) =>
                      redFlagSymptomsEnum.options.find(
                        (o) => o.value === v && v !== "none"
                      )?.header.toLowerCase()
                  )
                  ?.join?.(", ")}
                ]
              </>
            ) : (
              ""
            )}
          </p>
        </>
      );
    }
    setOpenMultiConditionDialog(true);
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Red Flag Signs & Symptoms
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div
                className="grid grid-flow-col auto-cols-[480px] gap-6 scroll-smooth overflow-auto"
                id="life-style"
              >
                {/* acuteMC */}
                <div>
                  <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-green.png)`,
                      }}
                    >
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Red Flag Symptoms
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] font-semibold text-gray-800">
                          {redFlagSymptomsEnum.question}
                          <br />
                          <span className="font-normal">
                            {redFlagSymptomsEnum.subquestion}{" "}
                          </span>
                        </p>
                        <div className="flex flex-col gap-2">
                          {redFlagSymptomsEnum.options.map((option) => {
                            return (
                              <>
                                <button
                                  onClick={() => {
                                    acuteMultiSelection(option.value);
                                  }}
                                  className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                      acuteMCVals?.includes(option.value)
                        ? "bg-gray-20 border-gray-600"
                        : "bg-white border-gray-100"
                    }`}
                                >
                                  <p className="font-semibold text-body-md flex justify-start items-start">
                                    {option.header}{" "}
                                  </p>
                                  <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                    {option.subHeader}
                                  </p>
                                </button>
                              </>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {showAnalysisCard && (
                  <div>
                    <div className="h-[585px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                      <div className="ml-8 h-[420px] w-[420px] bg-green-50 rounded-full flex items-center justify-center bg-opacity-40">
                        <ReactSVG
                          src={"/icons/analysis-pad.svg"}
                          className={``}
                        />
                      </div>
                      <div className="mx-7 my-5 text-center">
                        {!clinicalAnalysis && (
                          <Button
                            className="w-full !px-2 !text-xl"
                            type="primary"
                            text="Red Flag Signs & Symptoms Results"
                            onClick={() => {
                              handleAlerts();
                            }}
                          />
                        )}
                        {clinicalAnalysis && (
                          <h4 className="text-xl text-REELTeal font-bold items-center justify-center flex">
                            Red Flag Signs & Symptoms Review Complete
                          </h4>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <EndServiceModel
              open={openMultiConditionDialog}
              title="Red Flag Signs & Symptoms Alert"
              showEndServiceAndRef={showEndService}
              showOTCConsultAndRef={showOTC}
              className="w-[40%]"
              content={() => alterContent}
              handleClose={() => {
                setOpenMultiConditionDialog(false);
                setAlertConfirmation(0);
                setAlertExplaination("");
              }}
              handleContinue={(ref, exp) => {
                setAlertConfirmation(
                  ref
                    ? AlertConfirmationTypes.ContinueWithRefer
                    : AlertConfirmationTypes.ContinueWithNoReferral
                );
                setAlertExplaination(exp);
                if (ref !== undefined) {
                  setShowRef(ref);
                }
                setOpenMultiConditionDialog(false);
                updateStepsVisibility(false);
                setCompleteSubStep(true);
                setClinicalAnalysis(true);
              }}
              handleEndService={() => {
                setShowRef(true);
                setAlertConfirmation(AlertConfirmationTypes.EndService);
                setAlertExplaination("");
                setOpenMultiConditionDialog(false);
                updateStepsVisibility(true);
                setClickedEndService(true);
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4  rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px] ${
              !completeSubStep && "opacity-40 pointer-events-none"
            }`}
            onClick={() => {
              onNext();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default NonTypical;
